import React, { useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { AspectRatio, Flex, Link, useDisclosure } from '@chakra-ui/react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import _get from 'lodash/get';

import { Modal, Text, FormControl, Input, Card, Button, Icon } from 'components';
import {
  usePasswordType,
  takePendingDuoLogin,
  savePendingDuoLogin,
  getPreAuthState,
  waitForDuoPopup,
  openDuoPopup,
  DUO_DENIED_MESSAGE,
  INVALID_DUO_LOGIN_MESSAGE,
} from 'utils';
import { loginBG, GPPayLogoLogin } from 'assets';
import { useDB } from 'store';

import { loginDuo, getDuoLoginError, startSession } from './session';
import classes from './Login.styles.module.css';

interface LoginProps {}
// Staff (Admin) logins only. Property users sign in to the GuestPoint PMS, which applies their MFA,
// and use the GuestPoint Pay pages embedded there
interface ILogin {
  username: string;
  password: string;
}

// Where property users sign in. Unset hides the link
const GUESTPOINT_LOGIN_URL = process.env.REACT_APP_GUESTPOINT_LOGIN_URL || '';

interface LoginButtonProps {
  form: UseFormReturn<ILogin>;
  isLoading: boolean;
  loadingText: string;
}
const LoginButton: React.FC<LoginButtonProps> = React.memo((props) => {
  const {
    form: { control },
    isLoading,
    loadingText,
  } = props;

  const username = useWatch({ control, name: 'username' });
  const password = useWatch({ control, name: 'password' });

  const [autoFilled, setAutoFilled] = useState(false);

  useLayoutEffect(() => {
    const checkAutofilled = () => {
      const usernameField = document.querySelector('input[name="username"]');
      const pwField = document.querySelector('input[name="password"]');
      setAutoFilled(
        !!usernameField?.matches('*:-webkit-autofill') || !!pwField?.matches('*:-webkit-autofill'),
      );
    };
    // The time when it's ready is not very stable, so check few times
    setTimeout(checkAutofilled, 500);
    setTimeout(checkAutofilled, 1000);
    setTimeout(checkAutofilled, 2000);
  }, []);

  return (
    <Button
      isDisabled={autoFilled ? false : !username || !password}
      loadingText={loadingText}
      isLoading={isLoading}
      colorScheme="accent"
      label={'Continue'}
      variant="solid"
      width="100%"
      type="submit"
    />
  );
});

const useLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const mutateAsync = async (payload: ILogin) => {
    try {
      setIsLoading(true);
      const result = await useDB.getState().axios(
        {
          method: 'POST',
          url: '/login',
          data: {
            Username: payload.username,
            Password: payload.password,
          },
        },
        false,
      );
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, mutateAsync };
};

export const Login: React.FC<LoginProps> = () => {
  const { passwordType, togglePasswordType } = usePasswordType();
  const { mutateAsync, isLoading } = useLogin();

  const [searchParams] = useSearchParams();
  const username = searchParams.get('username');
  const serial = searchParams.get('serial');

  const navigate = useNavigate();

  const form = useForm<ILogin>({
    defaultValues: {
      username: '',
      password: '',
    },
  });
  const { handleSubmit, register, setFocus, reset } = form;

  // A link with a serial number is for a property user, who now signs in to GuestPoint
  useEffect(() => {
    if (serial && GUESTPOINT_LOGIN_URL) {
      window.location.replace(GUESTPOINT_LOGIN_URL);
    }
  }, [serial]);

  useEffect(() => {
    setFocus('username');
  }, [setFocus]);

  useEffect(() => {
    if (username) {
      reset({ username, password: '' });
    }
  }, [username, reset]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [errorText, setErrorText] = useState('');
  // Set while the Duo prompt is open in a popup or the browser is leaving for it
  const [duoPending, setDuoPending] = useState(false);
  // Set while the Duo prompt is open in a popup. Calling it stops waiting on the popup
  const [cancelDuoPopup, setCancelDuoPopup] = useState<() => void>();

  const showError = useCallback(
    (message: string) => {
      setErrorText(message);
      onOpen();
    },
    [onOpen],
  );

  // Admin users GuestPoint flags for a second factor must pass Duo before they get a session.
  // Returns the /login/duo result, or undefined if the login cannot continue in this page
  const completeDuo = useCallback(
    async (authUrl: string, preAuthToken: string) => {
      // Drop any stale redirect login so a popup that inherits this tab's
      // sessionStorage hands its result back here instead of finishing on its own
      takePendingDuoLogin();

      const state = getPreAuthState(preAuthToken);
      const wait = state ? waitForDuoPopup(state) : undefined;
      const popup = wait ? openDuoPopup(authUrl) : null;

      // No popup (blocked or unsupported), so fall back to sending this tab to Duo,
      // which returns to /login/duo-callback
      if (!state || !wait || !popup) {
        wait?.result.catch(() => {});
        wait?.cancel();
        savePendingDuoLogin({ preAuthToken });
        window.location.assign(authUrl);
        return undefined;
      }

      setCancelDuoPopup(() => wait.cancel);
      let duoResult;
      try {
        duoResult = await wait.result;
      } catch (error) {
        showError(error instanceof Error ? error.message : INVALID_DUO_LOGIN_MESSAGE);
        setDuoPending(false);
        return undefined;
      } finally {
        popup.close();
        setCancelDuoPopup(undefined);
      }

      if (duoResult.error || !duoResult.duoCode) {
        showError(
          duoResult.error
            ? duoResult.errorDescription ?? DUO_DENIED_MESSAGE
            : INVALID_DUO_LOGIN_MESSAGE,
        );
        setDuoPending(false);
        return undefined;
      }

      const result = await loginDuo({
        PreAuthToken: preAuthToken,
        State: state,
        DuoCode: duoResult.duoCode,
      });
      setDuoPending(false);
      if (result.error) {
        showError(getDuoLoginError(result));
        return undefined;
      }
      return result;
    },
    [showError],
  );

  const onSubmit = useCallback(
    async (data: ILogin) => {
      let result = await mutateAsync(data);
      if (result.error) {
        showError(result.message);
        return;
      }

      if (_get(result, 'data.data.MFARequired')) {
        setDuoPending(true);
        result = await completeDuo(
          _get(result, 'data.data.AuthURL', ''),
          _get(result, 'data.data.PreAuthToken', ''),
        );
        if (!result) {
          return;
        }
      }

      const sessionError = startSession(result, navigate);
      if (sessionError) {
        showError(sessionError);
      }
    },
    [mutateAsync, completeDuo, showError, navigate],
  );

  return (
    <>
      {isOpen && (
        <Modal
          size={'sm'}
          isOpen={isOpen}
          onClose={onClose}
          okProps={{ display: 'none' }}
          cancelText={'Close'}
          modalSpace={0}
          title={'Login Failed'}
        >
          {errorText}
        </Modal>
      )}
      <div className={classes.LoginImageContainer}>
        <Flex
          w={'40vw'}
          h={'100vh'}
          overflow={'hidden'}
          backgroundColor={'#FFF'}
          background={`url(${loginBG})`}
          backgroundSize="cover"
        />
      </div>
      <Flex
        h={'100vh'}
        w={'60vw'}
        overflow={'hidden'}
        backgroundColor={'#FFF'}
        className={classes.LoginCentre}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <AspectRatio w={'286.5px'} h={'48px'} mb={'32px'}>
              <img src={GPPayLogoLogin} alt={'GuestPoint Pay Logo'} />
            </AspectRatio>
            <Card
              size="sm"
              width="453px"
              minHeight="382px"
              filter={
                'drop-shadow(-2px -2px 8px rgba(0, 0, 0, 0.08)) drop-shadow(2px 2px 8px rgba(0, 0, 0, 0.08))'
              }
              boxShadow={'none'}
              p={'32px'}
            >
              <Text variant="heading05Bold" mb={4}>
                {'Sign In To Your Account'}
              </Text>
              <Flex direction={'column'}>
                <FormControl size="md" label={'Username'} mb={4}>
                  <Input {...register('username')} />
                </FormControl>
                <FormControl size="md" label={'Password'} mb={'18px'}>
                  <Input
                    rightElement={
                      <Icon
                        color="gray.700"
                        size={8}
                        variant="ViewInput"
                        onClick={togglePasswordType}
                        cursor={'pointer'}
                      />
                    }
                    type={passwordType}
                    {...register('password')}
                  />
                </FormControl>
                <LoginButton
                  form={form}
                  isLoading={isLoading || duoPending}
                  loadingText={duoPending ? 'Waiting for Duo' : 'Logging in'}
                />
                {cancelDuoPopup && (
                  <>
                    <Text variant="pSmallRegular" mt={3}>
                      {'Complete the Duo verification in the popup window to finish signing in.'}
                    </Text>
                    <Button
                      label={'Cancel'}
                      variant="outline"
                      width="100%"
                      mt={2}
                      onClick={cancelDuoPopup}
                    />
                  </>
                )}
                {GUESTPOINT_LOGIN_URL && (
                  <Text variant="pSmallRegular" mt={6}>
                    {'Property user? '}
                    <Link href={GUESTPOINT_LOGIN_URL} color="accent.700" fontWeight="semibold">
                      {'Sign in to GuestPoint'}
                    </Link>
                    {' and open GuestPoint Pay from there.'}
                  </Text>
                )}
              </Flex>
            </Card>
            <div style={{ lineHeight: '40px' }}>
              <Link
                href="https://www.guestpoint.com"
                target="_blank"
                variant="labelExtraSmallSemiBold"
                style={{ display: 'inline-block' }}
                paddingRight="8px"
              >
                <Text variant="labelExtraSmallSemiBold" mr="1" color="gray.800">
                  © GuestPoint
                </Text>
              </Link>
              <Text
                style={{ display: 'inline-block' }}
                variant="labelExtraSmallSemiBold"
                mr="1"
                color="gray.800"
              >
                &bull;
              </Text>
              <Link
                href="https://www.guestpoint.com/contact-us/"
                target="_blank"
                variant="labelExtraSmallSemiBold"
                style={{ display: 'inline-block' }}
                paddingLeft="8px"
                paddingRight="8px"
              >
                <Text variant="labelExtraSmallSemiBold" mr="1" color="gray.800">
                  Contact Support
                </Text>
              </Link>
              <Text
                style={{ display: 'inline-block' }}
                variant="labelExtraSmallSemiBold"
                mr="1"
                color="gray.700"
              >
                &bull;
              </Text>
              <Link
                href="https://www.guestpoint.com/terms"
                target="_blank"
                variant="labelExtraSmallSemiBold"
                style={{ display: 'inline-block' }}
                paddingLeft="8px"
              >
                <Text variant="labelExtraSmallSemiBold" mr="1" color="gray.800">
                  Terms of use
                </Text>
              </Link>
            </div>
          </div>
        </form>
      </Flex>
    </>
  );
};
